export type GameScreen = 'menu' | 'maps' | 'editor' | 'skins' | 'playing' | 'paused' | 'quiz' | 'quizFeedback' | 'result';
export type GameMode = 'runner' | 'explore';
export type ControlAction = 'left' | 'right' | 'forward' | 'backward' | 'jump';
export type EditorTab = 'settings' | 'terrain' | 'path' | 'items' | 'coins' | 'magnets' | 'supplements' | 'mounts' | 'obstacles' | 'gates' | 'scenery';
export type MapVisibility = 'private' | 'public';
export type MapTheme = 'neon' | 'mountain' | 'coast' | 'beach' | 'joseon-harbor' | 'ys-palace';
export type GateDesign = 'neon' | 'hanok';
export type MountKind = 'random' | 'palanquin' | 'horse' | 'ox' | 'tiger' | 'panokseon' | 'turtle-ship';
export type ObstacleKind = 'basic' | 'fence' | 'scarecrow' | 'fortress-wall';
export type RoadPieceKind = 'straight' | 'curve-left' | 'curve-right' | 's-left' | 's-right' | 'slope-up' | 'slope-down' | 'loop-one' | 'loop-two' | 'jump-wide' | 'jump-stairs' | 'jump-zigzag';
export type PathPresetKind = 'circle' | 'horizontal-s';
export type SceneryKind = 'tree' | 'pine-tree' | 'pine-tree-windswept' | 'pine-tree-young' | 'building' | 'cone' | 'hanok' | 'hanok-annex' | 'hanok-pavilion' | 'hanok-palace' | 'hanok-courtyard' | 'hanok-library' | 'hanok-study' | 'lotus-pond' | 'lotus-flower' | 'pond-rock' | 'stone-bridge' | 'stone-stairs' | 'stone-wall' | 'jangdok' | 'stone-lantern' | 'wooden-fence' | 'harbor-warehouse' | 'fishing-boat' | 'haechi-guide' | 'kim-yeeun-old-shrine-naejaesil' | 'kim-yeeun-gyeonghoeru' | 'kim-yeeun-base' | 'kim-yeeun-stairs' | 'kim-yeeun-stone-path-small2' | 'kim-yeeun-old-shrine-south-haenggak' | 'kim-yeeun-gwollaegaksa-annex' | 'kim-yeeun-stone-block' | 'kim-yeeun-stone-floor' | 'ys' | 'custom';
export type TerrainKind = 'grass' | 'earth' | 'stone' | 'sand' | 'water' | 'mountain';

export interface TerrainGrid {
    columns: number;
    rows: number;
    cells: TerrainKind[];
}

export interface MapGroundImage {
    dataUrl: string;
    fileName: string;
    width: number;
    height: number;
}

export interface QuizQuestion {
    id: string;
    question: string;
    answer: boolean;
    explanation: string;
    correctMessage: string;
    wrongMessage: string;
}

export interface MapWaypoint {
    id: string;
    name: string;
    distance: number;
    x: number;
    y: number;
    z: number;
    roadPiece?: RoadPieceKind;
    jumpGapAfter?: boolean;
}

export interface MapObjectPlacement {
    id: string;
    distance: number;
    lane: number;
    height: number;
    rotation: number;
    scale: number;
    obstacleKind?: ObstacleKind;
}

export interface MapGate extends MapObjectPlacement {
    name: string;
    design: GateDesign;
    quizEnabled: boolean;
    questions: QuizQuestion[];
}

export interface MapMountPlacement extends MapObjectPlacement {
    kind: MountKind;
}

export interface MapSceneryPlacement {
    id: string;
    name: string;
    kind: SceneryKind;
    x: number;
    y: number;
    z: number;
    rotation: number;
    scale: number;
    verticalScale?: number;
    assetId?: string;
    fileName?: string;
    autoOpen?: boolean;
    openRadius?: number;
    openHeight?: number;
    guideTargetId?: string;
    guideText?: string;
    interactionRadius?: number;
}

export interface GameMap {
    schemaVersion: 1;
    id: string;
    title: string;
    description: string;
    author: string;
    visibility: MapVisibility;
    theme: MapTheme;
    trackLength: number;
    trackWidth: number;
    mapWidth: number;
    mapDepth: number;
    terrain?: TerrainGrid;
    groundImage?: MapGroundImage;
    hiddenNpcCount: number;
    waypoints: MapWaypoint[];
    coins: MapObjectPlacement[];
    magnets: MapObjectPlacement[];
    supplements: MapObjectPlacement[];
    mounts: MapMountPlacement[];
    obstacles: MapObjectPlacement[];
    gates: MapGate[];
    scenery: MapSceneryPlacement[];
    createdAt: string;
    updatedAt: string;
    builtIn?: boolean;
}

export interface RuntimeGate extends MapGate {
    resolved?: boolean;
    selectedQuestion?: QuizQuestion;
    mesh?: any;
}

export interface AnswerRecord {
    gateId: string;
    gateName: string;
    question: string;
    selected: boolean;
    answer: boolean;
    correct: boolean;
    explanation: string;
}

export function createId(prefix: string): string {
    const random = Math.random().toString(36).slice(2, 9);
    return `${prefix}-${Date.now().toString(36)}-${random}`;
}

export function cloneMap(map: GameMap): GameMap {
    return JSON.parse(JSON.stringify(map));
}

export function normalizeMap(input: any): GameMap {
    if (!input || typeof input !== 'object') {
        throw new Error('맵 JSON 형식이 올바르지 않습니다.');
    }

    const now = new Date().toISOString();
    const sourceWaypoints = Array.isArray(input.waypoints) ? input.waypoints : [];
    const sourceCoins = Array.isArray(input.coins) ? input.coins : [];
    const sourceMagnets = Array.isArray(input.magnets) ? input.magnets : [];
    const sourceSupplements = Array.isArray(input.supplements) ? input.supplements : [];
    const sourceMounts = Array.isArray(input.mounts) ? input.mounts : [];
    const sourceObstacles = Array.isArray(input.obstacles) ? input.obstacles : [];
    const sourceGates = Array.isArray(input.gates) ? input.gates : [];
    const sourceScenery = Array.isArray(input.scenery) ? input.scenery : [];
    const trackLength = clamp(finite(input.trackLength, 900), 300, 10000);
    const mapWidth = clamp(finite(input.mapWidth, 600), 200, 20000);
    const mapDepth = clamp(finite(input.mapDepth, Math.max(1200, trackLength + 400)), 300, 20000);
    const terrainKinds: TerrainKind[] = ['grass', 'earth', 'stone', 'sand', 'water', 'mountain'];
    const defaultTerrainKind: TerrainKind = input.theme === 'beach'
        ? 'sand'
        : (input.theme === 'joseon-harbor' ? 'stone' : (input.theme === 'ys-palace' ? 'earth' : 'grass'));
    let terrain: TerrainGrid | undefined;
    if (input.terrain && typeof input.terrain === 'object') {
        const columns = Math.round(clamp(finite(input.terrain.columns, 36), 12, 64));
        const rows = Math.round(clamp(finite(input.terrain.rows, 48), 12, 96));
        const sourceCells = Array.isArray(input.terrain.cells) ? input.terrain.cells : [];
        terrain = {
            columns,
            rows,
            cells: Array.from({ length: columns * rows }, (_, index) =>
                terrainKinds.includes(sourceCells[index]) ? sourceCells[index] as TerrainKind : defaultTerrainKind
            )
        };
    }
    let groundImage: MapGroundImage | undefined;
    if (input.groundImage && typeof input.groundImage === 'object') {
        const dataUrl = String(input.groundImage.dataUrl || '');
        const supportedDataUrl = /^data:image\/(?:png|jpeg|webp);base64,[a-z0-9+/]+={0,2}$/i;
        if (dataUrl.length <= 640 * 1024 && supportedDataUrl.test(dataUrl)) {
            groundImage = {
                dataUrl,
                fileName: String(input.groundImage.fileName || '바닥 이미지').slice(0, 120),
                width: Math.round(clamp(finite(input.groundImage.width, 1), 1, 4096)),
                height: Math.round(clamp(finite(input.groundImage.height, 1), 1, 4096))
            };
        }
    }

    const placement = (item: any, prefix: string): MapObjectPlacement => ({
        id: String(item?.id || createId(prefix)),
        distance: finite(item?.distance, 40),
        lane: finite(item?.lane, 0),
        height: finite(item?.height, 0),
        rotation: finite(item?.rotation, 0),
        scale: clamp(finite(item?.scale, 1), 0.25, 4)
    });

    const roadPieceKinds: RoadPieceKind[] = ['straight', 'curve-left', 'curve-right', 's-left', 's-right', 'slope-up', 'slope-down', 'loop-one', 'loop-two', 'jump-wide', 'jump-stairs', 'jump-zigzag'];
    const waypoints = sourceWaypoints.map((item: any, index: number, list: any[]) => {
        const roadPiece = roadPieceKinds.includes(item?.roadPiece) ? item.roadPiece as RoadPieceKind : undefined;
        return {
            id: String(item?.id || createId('waypoint')),
            name: String(item?.name || `웨이포인트 ${index + 1}`),
            distance: clamp(finite(item?.distance, (index / Math.max(1, list.length - 1)) * trackLength), 0, trackLength),
            x: clamp(finite(item?.x, 0), -10000, 10000),
            y: clamp(finite(item?.y, 0), -6, 24),
            z: clamp(finite(item?.z, -clamp(finite(item?.distance, (index / Math.max(1, list.length - 1)) * trackLength), 0, trackLength)), -10000, 10000),
            roadPiece,
            jumpGapAfter: (roadPiece === 'jump-wide' || roadPiece === 'jump-stairs' || roadPiece === 'jump-zigzag')
                && item?.jumpGapAfter === true ? true : undefined
        };
    });

    while (waypoints.length < 4) {
        const index = waypoints.length;
        waypoints.push({
            id: createId('waypoint'),
            name: index === 0 ? '시작' : (index === 3 ? '골인' : `웨이포인트 ${index + 1}`),
            distance: (index / 3) * trackLength,
            x: 0,
            y: index % 2 === 0 ? 0 : 2,
            z: -(index / 3) * trackLength
        });
    }

    waypoints.sort((a, b) => a.distance - b.distance);
    waypoints[0].distance = 0;
    waypoints[waypoints.length - 1].distance = trackLength;
    for (let index = 1; index < waypoints.length - 1; index++) {
        const remaining = waypoints.length - 1 - index;
        waypoints[index].distance = clamp(waypoints[index].distance, waypoints[index - 1].distance + 1, trackLength - remaining);
    }

    const map: GameMap = {
        schemaVersion: 1,
        id: String(input.id || createId('map')),
        title: String(input.title || '새 퀴즈 러너 맵').slice(0, 80),
        description: String(input.description || '').slice(0, 500),
        author: String(input.author || '로컬 제작자').slice(0, 60),
        visibility: input.visibility === 'public' ? 'public' : 'private',
        theme: input.theme === 'mountain' || input.theme === 'coast' || input.theme === 'beach'
            || input.theme === 'joseon-harbor' || input.theme === 'ys-palace' ? input.theme : 'neon',
        trackLength,
        trackWidth: clamp(finite(input.trackWidth, 16), 10, 60),
        mapWidth,
        mapDepth,
        terrain,
        groundImage,
        hiddenNpcCount: Math.round(clamp(finite(input.hiddenNpcCount, 0), 0, 20)),
        waypoints,
        coins: sourceCoins.map((item: any) => placement(item, 'coin')),
        magnets: sourceMagnets.map((item: any) => placement(item, 'magnet')),
        supplements: sourceSupplements.map((item: any) => placement(item, 'supplement')),
        mounts: sourceMounts.map((item: any): MapMountPlacement => ({
            ...placement(item, 'mount'),
            kind: item?.kind === 'random' || item?.kind === 'horse' || item?.kind === 'ox' || item?.kind === 'tiger'
                || item?.kind === 'panokseon' || item?.kind === 'turtle-ship' ? item.kind : 'palanquin'
        })),
        obstacles: sourceObstacles.map((item: any) => ({
            ...placement(item, 'obstacle'),
            obstacleKind: item?.obstacleKind === 'fence' || item?.obstacleKind === 'scarecrow' || item?.obstacleKind === 'fortress-wall'
                ? item.obstacleKind
                : 'basic'
        })),
        gates: sourceGates.map((item: any, gateIndex: number) => {
            const base = placement(item, 'gate');
            const questions = (Array.isArray(item?.questions) ? item.questions : []).map((question: any, questionIndex: number) => ({
                id: String(question?.id || createId('quiz')),
                question: String(question?.question || `게이트 ${gateIndex + 1} 문제 ${questionIndex + 1}`),
                answer: question?.answer !== false,
                explanation: String(question?.explanation || '등록된 해설이 없습니다.'),
                correctMessage: String(question?.correctMessage || '정답입니다!'),
                wrongMessage: String(question?.wrongMessage || '오답입니다.')
            }));

            return {
                ...base,
                name: String(item?.name || `게이트 ${gateIndex + 1}`),
                design: item?.design === 'hanok' ? 'hanok' : 'neon',
                quizEnabled: item?.quizEnabled !== false,
                questions: questions.length > 0 ? questions : [createBlankQuestion()]
            };
        }),
        scenery: sourceScenery.map((item: any, index: number): MapSceneryPlacement => {
            const allowedKinds: SceneryKind[] = ['tree', 'pine-tree', 'pine-tree-windswept', 'pine-tree-young', 'building', 'cone', 'hanok', 'hanok-annex', 'hanok-pavilion', 'hanok-palace', 'hanok-courtyard', 'hanok-library', 'hanok-study', 'lotus-pond', 'lotus-flower', 'pond-rock', 'stone-bridge', 'stone-stairs', 'stone-wall', 'jangdok', 'stone-lantern', 'wooden-fence', 'harbor-warehouse', 'fishing-boat', 'haechi-guide', 'kim-yeeun-old-shrine-naejaesil', 'kim-yeeun-gyeonghoeru', 'kim-yeeun-base', 'kim-yeeun-stairs', 'kim-yeeun-stone-path-small2', 'kim-yeeun-old-shrine-south-haenggak', 'kim-yeeun-gwollaegaksa-annex', 'kim-yeeun-stone-block', 'kim-yeeun-stone-floor', 'ys', 'custom'];
            const kind: SceneryKind = allowedKinds.includes(item?.kind) ? item.kind : 'tree';
            return {
                id: String(item?.id || createId('scenery')),
                name: String(item?.name || `장식 ${index + 1}`).slice(0, 80),
                kind,
                x: clamp(finite(item?.x, 20), -10000, 10000),
                y: clamp(finite(item?.y, 0), -100, 100),
                z: clamp(finite(item?.z, -80), -10000, 10000),
                rotation: clamp(finite(item?.rotation, 0), -180, 180),
                scale: clamp(finite(item?.scale, 1), 0.1, 10),
                verticalScale: item?.verticalScale === undefined
                    ? undefined
                    : clamp(finite(item?.verticalScale, 1), 0.1, 10),
                assetId: kind === 'custom' || kind === 'ys' ? String(item?.assetId || '') : undefined,
                fileName: kind === 'custom' || kind === 'ys' ? String(item?.fileName || '') : undefined,
                autoOpen: kind === 'ys' && item?.autoOpen === true ? true : undefined,
                openRadius: kind === 'ys' && item?.autoOpen === true
                    ? clamp(finite(item?.openRadius, 14), 4, 40)
                    : undefined,
                openHeight: kind === 'ys' && item?.autoOpen === true
                    ? clamp(finite(item?.openHeight, 7), 2, 30)
                    : undefined,
                guideTargetId: kind === 'haechi-guide'
                    ? String(item?.guideTargetId || '').slice(0, 160) || undefined
                    : undefined,
                guideText: kind === 'haechi-guide'
                    ? String(item?.guideText || '').slice(0, 1000) || undefined
                    : undefined,
                interactionRadius: kind === 'haechi-guide'
                    ? clamp(finite(item?.interactionRadius, 4.5), 2, 12)
                    : undefined
            };
        }),
        createdAt: String(input.createdAt || now),
        updatedAt: now,
        builtIn: input.builtIn === true
    };

    const guideBuildingKinds: SceneryKind[] = [
        'building',
        'hanok',
        'hanok-annex',
        'hanok-pavilion',
        'hanok-palace',
        'hanok-courtyard',
        'hanok-library',
        'hanok-study',
        'harbor-warehouse',
        'ys',
        'custom'
    ];
    const sceneryById = new Map(map.scenery.map((item) => [item.id, item]));
    map.scenery.forEach((item) => {
        if (item.kind !== 'haechi-guide' || !item.guideTargetId) return;
        const target = sceneryById.get(item.guideTargetId);
        if (!target || target.id === item.id || !guideBuildingKinds.includes(target.kind)) {
            item.guideTargetId = undefined;
            item.guideText = undefined;
        }
    });

    map.coins.forEach((item) => clampPlacement(item, map));
    map.magnets.forEach((item) => clampPlacement(item, map));
    map.supplements.forEach((item) => clampPlacement(item, map));
    map.mounts.forEach((item) => clampPlacement(item, map));
    map.obstacles.forEach((item) => clampPlacement(item, map));
    map.gates.forEach((item) => clampPlacement(item, map));
    map.gates.sort((a, b) => a.distance - b.distance);
    return map;
}

export function createBlankQuestion(): QuizQuestion {
    return {
        id: createId('quiz'),
        question: '새 OX 질문을 입력하세요.',
        answer: true,
        explanation: '정답 해설을 입력하세요.',
        correctMessage: '정답입니다!',
        wrongMessage: '다시 생각해보세요.'
    };
}

export function createBlankPlacement(prefix: string, distance: number): MapObjectPlacement {
    return {
        id: createId(prefix),
        distance,
        lane: 0,
        height: 0,
        rotation: 0,
        scale: 1
    };
}

export function insertRoadPiece(map: GameMap, kind: RoadPieceKind, distance: number, anchor?: { x: number; z: number }): number {
    if (map.waypoints.length < 2) return -1;
    const selectedDistance = clamp(distance, 1, map.trackLength - 1);
    const segmentIndex = Math.max(0, map.waypoints.findIndex((item) => item.distance >= selectedDistance) - 1);
    const insertIndex = segmentIndex + 1;
    const previous = map.waypoints[segmentIndex];
    const next = map.waypoints[segmentIndex + 1];
    const point = (pointDistance: number, xOffset: number, yOffset: number, name: string): MapWaypoint => {
        const progress = clamp((pointDistance - previous.distance) / Math.max(1, next.distance - previous.distance), 0, 1);
        return {
            id: createId('waypoint'),
            name,
            distance: pointDistance,
            x: previous.x + (next.x - previous.x) * progress + xOffset,
            y: clamp(previous.y + (next.y - previous.y) * progress + yOffset, -6, 24),
            z: previous.z + (next.z - previous.z) * progress,
            roadPiece: kind
        };
    };

    const gap = Math.max(2, next.distance - previous.distance);

    if (kind === 'jump-wide' || kind === 'jump-stairs' || kind === 'jump-zigzag') {
        const platformCount = kind === 'jump-wide' ? 4 : 5;
        const platformLength = kind === 'jump-wide' ? 8 : (kind === 'jump-stairs' ? 7 : 7.5);
        const jumpGapLength = kind === 'jump-stairs' ? 3 : 3.2;
        const requiredSpan = platformCount * platformLength + (platformCount - 1) * jumpGapLength;
        if (gap < requiredSpan + 2) return -1;

        const selectedProgress = clamp((selectedDistance - previous.distance) / Math.max(1, gap), 0, 1);
        const interpolatedX = previous.x + (next.x - previous.x) * selectedProgress;
        const interpolatedZ = previous.z + (next.z - previous.z) * selectedProgress;
        const anchorX = finite(anchor?.x, interpolatedX);
        const anchorZ = finite(anchor?.z, interpolatedZ);
        const anchorDeltaX = anchorX - interpolatedX;
        const anchorDeltaZ = anchorZ - interpolatedZ;
        const horizontalLength = Math.hypot(next.x - previous.x, next.z - previous.z);
        const forwardX = horizontalLength > 0.001 ? (next.x - previous.x) / horizontalLength : 0;
        const forwardZ = horizontalLength > 0.001 ? (next.z - previous.z) / horizontalLength : -1;
        const rightX = -forwardZ;
        const rightZ = forwardX;
        const spanStart = clamp(
            selectedDistance - requiredSpan / 2,
            previous.distance + 1,
            next.distance - requiredSpan - 1
        );
        const label = kind === 'jump-wide'
            ? '넓은 징검발판'
            : (kind === 'jump-stairs' ? '완만한 계단 발판' : '넓은 지그재그 발판');
        const jumpPoints: MapWaypoint[] = [];

        for (let platformIndex = 0; platformIndex < platformCount; platformIndex++) {
            const startDistance = spanStart + platformIndex * (platformLength + jumpGapLength);
            const endDistance = startDistance + platformLength;
            const lateralOffset = kind === 'jump-zigzag'
                ? [-2.6, 2.6, -2.6, 2.6, 0][platformIndex]
                : 0;
            const heightOffset = kind === 'jump-stairs'
                ? [0, 0.75, 1.5, 0.75, 0][platformIndex]
                : (kind === 'jump-wide' ? [0, 0.45, 0, 0.45][platformIndex] : [0, 0.35, 0.7, 0.35, 0][platformIndex]);
            const centerDistance = (startDistance + endDistance) / 2;
            const centerProgress = clamp((centerDistance - previous.distance) / Math.max(1, next.distance - previous.distance), 0, 1);
            const platformY = clamp(previous.y + (next.y - previous.y) * centerProgress + heightOffset, -6, 24);
            const buildPlatformEdge = (pointDistance: number, edge: '시작' | '끝', jumpGapAfter?: boolean): MapWaypoint => {
                const progress = clamp((pointDistance - previous.distance) / Math.max(1, next.distance - previous.distance), 0, 1);
                return {
                    id: createId('waypoint'),
                    name: platformIndex === 0 && edge === '시작'
                        ? `${label} · 시작`
                        : `${label} · 발판 ${platformIndex + 1} ${edge}`,
                    distance: pointDistance,
                    x: previous.x + (next.x - previous.x) * progress + anchorDeltaX + rightX * lateralOffset,
                    y: platformY,
                    z: previous.z + (next.z - previous.z) * progress + anchorDeltaZ + rightZ * lateralOffset,
                    roadPiece: kind,
                    jumpGapAfter
                };
            };
            jumpPoints.push(buildPlatformEdge(startDistance, '시작'));
            jumpPoints.push(buildPlatformEdge(
                endDistance,
                '끝',
                platformIndex < platformCount - 1 ? true : undefined
            ));
        }

        map.waypoints.splice(insertIndex, 0, ...jumpPoints);
        return insertIndex;
    }

    if (kind === 'loop-one' || kind === 'loop-two') {
        const loopCount = kind === 'loop-two' ? 2 : 1;
        const segmentsPerLoop = 12;
        const pointCount = loopCount * (segmentsPerLoop + 1);
        if (gap < pointCount + 2) return -1;

        const selectedProgress = clamp((selectedDistance - previous.distance) / Math.max(1, gap), 0, 1);
        const interpolatedX = previous.x + (next.x - previous.x) * selectedProgress;
        const interpolatedY = previous.y + (next.y - previous.y) * selectedProgress;
        const interpolatedZ = previous.z + (next.z - previous.z) * selectedProgress;
        const anchorX = finite(anchor?.x, interpolatedX);
        const anchorZ = finite(anchor?.z, interpolatedZ);
        const horizontalLength = Math.hypot(next.x - previous.x, next.z - previous.z);
        const forwardX = horizontalLength > 0.001 ? (next.x - previous.x) / horizontalLength : 0;
        const forwardZ = horizontalLength > 0.001 ? (next.z - previous.z) / horizontalLength : -1;
        const availableRadius = Math.max(4, (24 - interpolatedY) / 2);
        const radius = clamp(Math.min(map.trackWidth * 0.38, availableRadius), 4, 9.5);
        const loopSpacing = radius * 2.65;
        const requestedSpan = Math.max(pointCount, loopCount * 58);
        const span = Math.min(gap - 2, requestedSpan);
        const spanStart = clamp(selectedDistance - span / 2, previous.distance + 1, next.distance - span - 1);
        const loopPoints: MapWaypoint[] = [];

        for (let loopIndex = 0; loopIndex < loopCount; loopIndex++) {
            const baseOffset = (loopIndex - (loopCount - 1) / 2) * loopSpacing;
            for (let segment = 0; segment <= segmentsPerLoop; segment++) {
                const sequenceIndex = loopIndex * (segmentsPerLoop + 1) + segment;
                const angle = (segment / segmentsPerLoop) * Math.PI * 2;
                const forwardOffset = baseOffset + Math.sin(angle) * radius;
                const heightOffset = (1 - Math.cos(angle)) * radius;
                const name = sequenceIndex === 0
                    ? `${loopCount}회전 루프 · 진입`
                    : (sequenceIndex === pointCount - 1
                        ? `${loopCount}회전 루프 · 완료`
                        : `${loopCount}회전 루프 · ${loopIndex + 1}바퀴 ${segment}/${segmentsPerLoop}`);
                loopPoints.push({
                    id: createId('waypoint'),
                    name,
                    distance: spanStart + (sequenceIndex / Math.max(1, pointCount - 1)) * span,
                    x: anchorX + forwardX * forwardOffset,
                    y: clamp(interpolatedY + heightOffset, -6, 24),
                    z: anchorZ + forwardZ * forwardOffset,
                    roadPiece: kind
                });
            }
        }

        map.waypoints.splice(insertIndex, 0, ...loopPoints);
        return insertIndex;
    }

    const beforeDistance = clamp(selectedDistance - Math.min(20, gap * 0.18), previous.distance + 0.5, selectedDistance - 0.5);
    const afterDistance = clamp(selectedDistance + Math.min(20, gap * 0.18), selectedDistance + 0.5, next.distance - 0.5);

    const pieces: Partial<Record<RoadPieceKind, MapWaypoint[]>> = {
        straight: [point(selectedDistance, 0, 0, '직선 조각')],
        'curve-left': [point(selectedDistance, -12, 0, '왼쪽 곡선')],
        'curve-right': [point(selectedDistance, 12, 0, '오른쪽 곡선')],
        's-left': [point(beforeDistance, -10, 0, '왼쪽 S곡선 1'), point(afterDistance, 10, 0, '왼쪽 S곡선 2')],
        's-right': [point(beforeDistance, 10, 0, '오른쪽 S곡선 1'), point(afterDistance, -10, 0, '오른쪽 S곡선 2')],
        'slope-up': [point(selectedDistance, 0, 5, '오르막 조각')],
        'slope-down': [point(selectedDistance, 0, -5, '내리막 조각')]
    };

    const selectedPieces = pieces[kind] || [];
    map.waypoints.splice(insertIndex, 0, ...selectedPieces);
    return insertIndex + selectedPieces.length - 1;
}

export function createPathPresetWaypoints(map: GameMap, kind: PathPresetKind, circleLaps: number = 1): MapWaypoint[] {
    if (kind === 'circle') {
        const laps = Math.round(clamp(finite(circleLaps, 1), 1, 10));
        const pointsPerLap = 16;
        const count = laps * pointsPerLap + 1;
        const radius = Math.max(map.trackWidth * 0.75, map.trackLength / (Math.PI * 2 * laps));
        return Array.from({ length: count }, (_, index) => {
            const progress = index / (count - 1);
            const angle = Math.PI / 2 - progress * Math.PI * 2 * laps;
            const currentLap = Math.min(laps, Math.max(1, Math.ceil(index / pointsPerLap)));
            const lapPoint = index === 0 ? 0 : ((index - 1) % pointsPerLap) + 1;
            return {
                id: createId('waypoint'),
                name: index === 0
                    ? '시작'
                    : (index === count - 1 ? '골인' : `원형 ${currentLap}바퀴 · ${lapPoint}`),
                distance: progress * map.trackLength,
                x: Math.cos(angle) * radius,
                y: 0,
                z: Math.sin(angle) * radius
            };
        });
    }

    const count = 15;
    const span = map.trackLength * 0.72;
    const amplitude = Math.max(55, map.trackLength * 0.14);
    return Array.from({ length: count }, (_, index) => {
        const progress = index / (count - 1);
        return {
            id: createId('waypoint'),
            name: index === 0 ? '시작' : (index === count - 1 ? '골인' : `가로 S ${index}`),
            distance: progress * map.trackLength,
            x: (progress - 0.5) * span,
            y: 0,
            z: Math.sin(progress * Math.PI * 2) * amplitude
        };
    });
}

export function createDefaultMap(): GameMap {
    const now = new Date().toISOString();
    const trackLength = 1120;
    const gates: MapGate[] = [
        {
            ...createBlankPlacement('gate', 270),
            id: 'gate-science',
            name: '게이트 1 · 우주 지식',
            design: 'hanok',
            quizEnabled: true,
            questions: [
                question('science-1', '지구는 태양 주위를 돈다.', true, '지구는 약 365일에 한 번 태양 주위를 공전합니다.'),
                question('science-2', '달은 스스로 빛을 만들어 낸다.', false, '달빛은 달 표면에서 반사된 태양빛입니다.'),
                question('science-3', '빛은 소리보다 빠르게 이동한다.', true, '빛은 진공에서 소리보다 훨씬 빠르게 이동합니다.')
            ]
        },
        {
            ...createBlankPlacement('gate', 570),
            id: 'gate-safety',
            name: '게이트 2 · 생활 안전',
            design: 'neon',
            quizEnabled: true,
            questions: [
                question('safety-1', '화재가 발생한 건물에서는 엘리베이터로 대피해야 한다.', false, '화재 시에는 비상계단을 이용해 대피해야 합니다.'),
                question('safety-2', '자전거를 탈 때 안전모를 착용하면 머리 부상 위험을 줄일 수 있다.', true, '안전모는 충격을 흡수해 머리 부상 위험을 낮춥니다.'),
                question('safety-3', '젖은 손으로 전기 플러그를 만져도 괜찮다.', false, '전기 제품은 반드시 마른 손으로 다뤄야 합니다.')
            ]
        },
        {
            ...createBlankPlacement('gate', 865),
            id: 'gate-environment',
            name: '게이트 3 · 지구 환경',
            design: 'hanok',
            quizEnabled: true,
            questions: [
                question('environment-1', '사용하지 않는 조명을 끄면 에너지를 절약할 수 있다.', true, '불필요한 전력 소비를 줄이면 탄소 배출도 줄일 수 있습니다.'),
                question('environment-2', '재활용품은 내용물이 남아 있어도 그대로 배출하는 것이 좋다.', false, '내용물을 비우고 헹군 뒤 재질별로 분리해야 합니다.'),
                question('environment-3', '나무는 대기 중 이산화탄소를 흡수하는 데 도움을 준다.', true, '나무는 광합성 과정에서 이산화탄소를 흡수합니다.')
            ]
        }
    ];

    const lanes = [-4.8, 0, 4.8];
    const coins: MapObjectPlacement[] = Array.from({ length: 50 }, (_, index) => {
        let distance = 36 + index * 21;
        if (gates.some((gate) => Math.abs(gate.distance - distance) < 11)) distance += 12;
        return {
            ...createBlankPlacement('coin', distance),
            id: `coin-${index + 1}`,
            lane: lanes[(index * 2 + Math.floor(index / 4)) % lanes.length]
        };
    });

    const obstacleDistances = [138, 205, 328, 418, 605, 690, 846, 938, 1018];
    const obstacleLanes = [0, -4.5, 4.5, 0, 4.5, -4.5, 0, -4.5, 4.5];

    return {
        schemaVersion: 1,
        id: 'builtin-neon-learning-run',
        title: '네온 러닝 아카데미',
        description: '세 가지 지식 게이트를 통과하는 기본 제공 곡선 코스입니다.',
        author: 'Quiz Rush 3D',
        visibility: 'public',
        theme: 'mountain',
        trackLength,
        trackWidth: 16,
        mapWidth: 600,
        mapDepth: 1600,
        hiddenNpcCount: 3,
        waypoints: [
            waypoint('start', '시작', 0, 0, 0),
            waypoint('w1', '첫 번째 곡선', 140, 8, 2.5),
            waypoint('w2', '사이언스 언덕', 280, -9, 5.5),
            waypoint('w3', '긴 내리막', 420, -14, 0.5),
            waypoint('w4', '세이프티 커브', 560, 12, 4),
            waypoint('w5', '스카이 점프', 700, 16, 7),
            waypoint('w6', '환경 협곡', 840, -10, 1.5),
            waypoint('w7', '마지막 언덕', 980, 7, 5),
            waypoint('goal', '골인', trackLength, 0, 1)
        ],
        coins,
        magnets: [
            { ...createBlankPlacement('magnet', 42), id: 'magnet-1', lane: 0 },
            { ...createBlankPlacement('magnet', 370), id: 'magnet-2', lane: 4.5 },
            { ...createBlankPlacement('magnet', 750), id: 'magnet-3', lane: -4.5 }
        ],
        supplements: [
            { ...createBlankPlacement('supplement', 180), id: 'supplement-1', lane: 0 },
            { ...createBlankPlacement('supplement', 495), id: 'supplement-2', lane: 4.5 },
            { ...createBlankPlacement('supplement', 790), id: 'supplement-3', lane: -4.5 }
        ],
        mounts: [
            { ...createBlankPlacement('mount', 110), id: 'mount-palanquin-1', kind: 'palanquin', lane: -4.5 },
            { ...createBlankPlacement('mount', 455), id: 'mount-horse-1', kind: 'horse', lane: 0 },
            { ...createBlankPlacement('mount', 820), id: 'mount-ox-1', kind: 'ox', lane: 4.5 },
            { ...createBlankPlacement('mount', 1030), id: 'mount-tiger-1', kind: 'tiger', lane: 0 }
        ],
        obstacles: obstacleDistances.map((distance, index) => ({
            ...createBlankPlacement('obstacle', distance),
            id: `obstacle-${index + 1}`,
            lane: obstacleLanes[index],
            obstacleKind: (['basic', 'fence', 'scarecrow', 'fortress-wall'] as ObstacleKind[])[index % 4]
        })),
        gates,
        scenery: [
            { id: 'tree-1', name: '코스 나무 1', kind: 'tree', x: -24, y: 0, z: -120, rotation: -12, scale: 1.2 },
            { id: 'tree-2', name: '코스 나무 2', kind: 'tree', x: 28, y: 0, z: -470, rotation: 18, scale: 1.5 },
            { id: 'tree-3', name: '코스 나무 3', kind: 'tree', x: -30, y: 0, z: -780, rotation: 8, scale: 1.35 },
            { id: 'building-1', name: '네온 타워', kind: 'building', x: 34, y: 0, z: -250, rotation: -8, scale: 1.15 },
            { id: 'building-2', name: '러닝 센터', kind: 'building', x: -38, y: 0, z: -690, rotation: 12, scale: 0.9 },
            { id: 'hanok-1', name: '궁궐 전각', kind: 'hanok-palace', x: -24, y: 0, z: -72, rotation: 14, scale: 0.9 },
            { id: 'hanok-annex-1', name: '사랑채', kind: 'hanok-annex', x: 31, y: 0, z: -188, rotation: -10, scale: 1.05 },
            { id: 'pavilion-1', name: '팔각 정자', kind: 'hanok-pavilion', x: -30, y: 0, z: -335, rotation: 8, scale: 1.1 },
            { id: 'library-1', name: '궁원 이층 누각', kind: 'hanok-library', x: 34, y: 0, z: -515, rotation: -7, scale: 0.82 },
            { id: 'study-1', name: '선비 서재', kind: 'hanok-study', x: -29, y: 0, z: -640, rotation: 12, scale: 1.05 },
            { id: 'pond-1', name: '정자 연꽃 연못', kind: 'lotus-pond', x: -39, y: 0, z: -352, rotation: -8, scale: 1.1 },
            { id: 'bridge-1', name: '연못 돌다리', kind: 'stone-bridge', x: -39, y: 0, z: -352, rotation: 78, scale: 0.72 },
            { id: 'jangdok-1', name: '장독대', kind: 'jangdok', x: -16, y: 0, z: -82, rotation: -8, scale: 1.1 },
            { id: 'lantern-1', name: '돌등', kind: 'stone-lantern', x: 18, y: 0, z: -108, rotation: 0, scale: 1 },
            { id: 'fence-1', name: '전통 목책', kind: 'wooden-fence', x: 24, y: 0, z: -142, rotation: -12, scale: 1.1 }
        ],
        createdAt: now,
        updatedAt: now,
        builtIn: true
    };
}

export function createJoseonHarborMap(): GameMap {
    const map = cloneMap(createDefaultMap());
    const now = new Date().toISOString();
    map.id = 'builtin-joseon-harbor-run';
    map.title = '조선 나루터 탐방길';
    map.description = '돌길을 따라 객주와 창고, 정자와 어선을 둘러보는 조선시대 항구 코스입니다.';
    map.author = 'Quiz Rush 3D · 역사 테마';
    map.theme = 'joseon-harbor';
    map.hiddenNpcCount = 5;
    map.waypoints = [
        waypoint('harbor-start', '나루터 입구', 0, -4, 0),
        waypoint('harbor-w1', '돌담 굽이', 140, 9, 0.5),
        waypoint('harbor-w2', '객주 앞', 280, 18, 1.2),
        waypoint('harbor-w3', '창고 거리', 420, 6, 0),
        waypoint('harbor-w4', '선착장 곡선', 560, -15, 0.8),
        waypoint('harbor-w5', '어시장', 700, -20, 0.2),
        waypoint('harbor-w6', '정자 언덕', 840, -7, 2),
        waypoint('harbor-w7', '관아 앞', 980, 12, 0.8),
        waypoint('harbor-goal', '포구 대문', map.trackLength, 4, 0)
    ];
    map.gates = map.gates.map((gate, index) => ({
        ...gate,
        id: `harbor-gate-${index + 1}`,
        design: 'hanok',
        name: [`나루터 지식문`, `생활문화 지식문`, `바다환경 지식문`][index] || gate.name
    }));
    map.mounts = [
        { ...createBlankPlacement('mount', 96), id: 'harbor-mount-palanquin', kind: 'palanquin', lane: -4.2 },
        { ...createBlankPlacement('mount', 300), id: 'harbor-mount-panokseon', kind: 'panokseon', lane: 0 },
        { ...createBlankPlacement('mount', 470), id: 'harbor-mount-horse', kind: 'horse', lane: 4.2 },
        { ...createBlankPlacement('mount', 780), id: 'harbor-mount-ox', kind: 'ox', lane: 0 },
        { ...createBlankPlacement('mount', 910), id: 'harbor-mount-turtle-ship', kind: 'turtle-ship', lane: 4.2 },
        { ...createBlankPlacement('mount', 1010), id: 'harbor-mount-tiger', kind: 'tiger', lane: -4.2 }
    ];
    map.scenery = [
        { id: 'harbor-house-1', name: '나루터 한옥 마당', kind: 'hanok-courtyard', x: -29, y: 0, z: -75, rotation: 8, scale: 0.72 },
        { id: 'harbor-annex-1', name: '객주 사랑채', kind: 'hanok-annex', x: 30, y: 0, z: -180, rotation: -12, scale: 1.25 },
        { id: 'harbor-pavilion-1', name: '바다 정자', kind: 'hanok-pavilion', x: -29, y: 0, z: -305, rotation: 14, scale: 1.15 },
        { id: 'harbor-store-1', name: '포구 목조 창고', kind: 'harbor-warehouse', x: 32, y: 0, z: -430, rotation: -6, scale: 1.25 },
        { id: 'harbor-store-2', name: '어물 창고', kind: 'harbor-warehouse', x: 38, y: 0, z: -565, rotation: 8, scale: 0.95 },
        { id: 'harbor-study-1', name: '포구 서재', kind: 'hanok-study', x: -32, y: 0, z: -690, rotation: 11, scale: 1.02 },
        { id: 'harbor-library-1', name: '나루터 누각', kind: 'hanok-library', x: 35, y: 0, z: -820, rotation: -9, scale: 0.78 },
        { id: 'harbor-pond-1', name: '관아 연꽃 연못', kind: 'lotus-pond', x: -36, y: 0, z: -930, rotation: 5, scale: 0.9 },
        { id: 'harbor-boat-1', name: '돛단 어선', kind: 'fishing-boat', x: 46, y: 0, z: -250, rotation: -18, scale: 1.2 },
        { id: 'harbor-boat-2', name: '나룻배', kind: 'fishing-boat', x: 52, y: 0, z: -610, rotation: 12, scale: 0.9 },
        { id: 'harbor-jangdok-1', name: '객주 장독대', kind: 'jangdok', x: -20, y: 0, z: -200, rotation: 0, scale: 1.05 },
        { id: 'harbor-fence-1', name: '선착장 목책', kind: 'wooden-fence', x: 24, y: 0, z: -340, rotation: -8, scale: 1.4 },
        { id: 'harbor-lantern-1', name: '포구 돌등', kind: 'stone-lantern', x: -18, y: 0, z: -510, rotation: 0, scale: 1 }
    ];
    map.createdAt = now;
    map.updatedAt = now;
    map.builtIn = true;
    return map;
}

export function createYsPalaceCourseMap(): GameMap {
    const now = new Date().toISOString();
    const coursePlan = [
        { id: 'palace-start', name: '북서 출발점', x: -72, z: -145 },
        { id: 'palace-turn-1', name: '첫 번째 남쪽 꺾임', x: -72, z: -120 },
        { id: 'palace-turn-2', name: '북쪽 긴 직선 끝', x: 86, z: -120 },
        { id: 'palace-turn-3', name: '동쪽 세로길 끝', x: 86, z: 40 },
        { id: 'palace-turn-4', name: '중앙 진입', x: 12, z: 40 },
        { id: 'palace-turn-5', name: '중앙 북쪽 꺾임', x: 12, z: -48 },
        { id: 'palace-turn-6', name: '서쪽 반환점', x: -70, z: -48 },
        { id: 'palace-turn-7', name: '서쪽 세로길 끝', x: -70, z: 130 },
        { id: 'palace-turn-8', name: '남쪽 긴 직선 끝', x: 58, z: 130 },
        { id: 'palace-goal', name: '남쪽 골인점', x: 58, z: 152 }
    ];
    let accumulatedDistance = 0;
    const waypoints: MapWaypoint[] = coursePlan.map((point, index) => {
        if (index > 0) {
            const previous = coursePlan[index - 1];
            accumulatedDistance += Math.hypot(point.x - previous.x, point.z - previous.z);
        }
        return {
            id: point.id,
            name: point.name,
            distance: accumulatedDistance,
            x: point.x,
            y: 0,
            z: point.z
        };
    });
    const trackLength = accumulatedDistance;
    const scenery: MapSceneryPlacement[] = [];
    const ys = (
        id: string,
        name: string,
        assetId: string,
        x: number,
        z: number,
        rotation: number,
        scale: number,
        options: Pick<MapSceneryPlacement, 'verticalScale' | 'autoOpen' | 'openRadius' | 'openHeight'> = {}
    ): MapSceneryPlacement => ({
        id,
        name,
        kind: 'ys',
        x,
        y: 0,
        z,
        rotation,
        scale,
        assetId,
        fileName: `${assetId}.glb`,
        ...options
    });
    const addWallRun = (
        idPrefix: string,
        namePrefix: string,
        horizontal: boolean,
        fixed: number,
        start: number,
        end: number
    ) => {
        const length = Math.abs(end - start);
        const count = Math.max(1, Math.ceil(length / 24));
        const spacing = length / count;
        const scale = clamp((spacing / 12.33585) * 1.04, 1.65, 2.35);
        for (let index = 0; index < count; index++) {
            const position = Math.min(start, end) + spacing * (index + 0.5);
            scenery.push(ys(
                `ys-palace-background-wall-${idPrefix}-${index + 1}`,
                `${namePrefix} 담장 ${index + 1}`,
                'yeonkyung-fence',
                horizontal ? position : fixed,
                horizontal ? fixed : position,
                horizontal ? 90 : 0,
                scale,
                { verticalScale: 5 }
            ));
        }
    };

    const minX = -125;
    const maxX = 125;
    const minZ = -180;
    const maxZ = 180;
    const gateHalfGap = 7.5;
    addWallRun('north-west', '북문 서편', true, minZ, minX, -gateHalfGap);
    addWallRun('north-east', '북문 동편', true, minZ, gateHalfGap, maxX);
    addWallRun('south-west', '남문 서편', true, maxZ, minX, -gateHalfGap);
    addWallRun('south-east', '남문 동편', true, maxZ, gateHalfGap, maxX);
    addWallRun('west-north', '서문 북편', false, minX, minZ, -gateHalfGap);
    addWallRun('west-south', '서문 남편', false, minX, gateHalfGap, maxZ);
    addWallRun('east-north', '동문 북편', false, maxX, minZ, -gateHalfGap);
    addWallRun('east-south', '동문 남편', false, maxX, gateHalfGap, maxZ);

    const autoGate = { autoOpen: true, openRadius: 16, openHeight: 7.5 };
    scenery.push(
        ys('ys-palace-background-gate-north', '북문 · 연경당 수인문', 'yeonkyung-sooinmun', 0, minZ, 0, 2.5, autoGate),
        ys('ys-palace-background-gate-east', '동문 · 연경당 수인문', 'yeonkyung-sooinmun', maxX, 0, -90, 2.5, autoGate),
        ys('ys-palace-background-gate-south', '남문 · 연경당 수인문', 'yeonkyung-sooinmun', 0, maxZ, 180, 2.5, autoGate),
        ys('ys-palace-background-gate-west', '서문 · 연경당 수인문', 'yeonkyung-sooinmun', minX, 0, 90, 2.5, autoGate)
    );

    const lanes = [-3.6, 0, 3.6];
    const coins = Array.from({ length: 34 }, (_, index) => ({
        ...createBlankPlacement('palace-coin', 30 + index * 25),
        id: `palace-coin-${index + 1}`,
        lane: lanes[index % lanes.length]
    }));

    return {
        schemaVersion: 1,
        id: 'builtin-ys-palace-course',
        title: 'YS 궁궐 담장 코스',
        description: '도면의 직각 왕복 코스를 따라 달리고, 사방 자동문으로 담장 안팎을 오갈 수 있는 궁궐 맵입니다.',
        author: 'Quiz Rush 3D · YS',
        visibility: 'public',
        theme: 'ys-palace',
        trackLength,
        trackWidth: 14,
        mapWidth: 300,
        mapDepth: 420,
        terrain: {
            columns: 30,
            rows: 42,
            cells: Array.from({ length: 30 * 42 }, () => 'earth' as TerrainKind)
        },
        hiddenNpcCount: 0,
        waypoints,
        coins,
        magnets: [
            { ...createBlankPlacement('palace-magnet', 285), id: 'palace-magnet-1', lane: -3.6 },
            { ...createBlankPlacement('palace-magnet', 685), id: 'palace-magnet-2', lane: 3.6 }
        ],
        supplements: [
            { ...createBlankPlacement('palace-supplement', 485), id: 'palace-supplement-1', lane: 0 }
        ],
        mounts: [],
        obstacles: [],
        gates: [],
        scenery,
        createdAt: now,
        updatedAt: now,
        builtIn: true
    };
}

function question(id: string, text: string, answer: boolean, explanation: string): QuizQuestion {
    return {
        id,
        question: text,
        answer,
        explanation,
        correctMessage: '정답입니다!',
        wrongMessage: '오답입니다.'
    };
}

function waypoint(id: string, name: string, distance: number, x: number, y: number): MapWaypoint {
    return { id, name, distance, x, y, z: -distance };
}

function clampPlacement(item: MapObjectPlacement, map: GameMap) {
    item.distance = clamp(item.distance, 10, map.trackLength - 10);
    item.lane = clamp(item.lane, -(map.trackWidth / 2 - 0.8), map.trackWidth / 2 - 0.8);
    item.height = clamp(item.height, -2, 12);
    item.rotation = clamp(item.rotation, -180, 180);
    item.scale = clamp(item.scale, 0.25, 4);
}

function finite(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
